import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { LeadAccessCheck } from "@/components/MetaLeadAccess";

const Totals = ({ imp }) => {
  if (!imp) return <div className="text-zinc-500">No import run yet.</div>;
  if (imp.status === "running") {
    const leads = (imp.pages || []).reduce((n, p) => n + (p.leads || 0), 0);
    return <div className="inline-flex items-center gap-1.5 text-zinc-700"><Loader2 className="h-3 w-3 animate-spin" />Importing… {leads} lead(s) read so far</div>;
  }
  const t = imp.totals || {};
  return (
    <div className="space-y-1" data-testid="meta-leads-import-result">
      <div className="text-zinc-700">
        Last import {fmtDateTime(imp.finished_at || imp.started_at)}: {t.leads ?? 0} lead(s) from {t.forms ?? 0} form(s) —
        <b> {t.created ?? 0} added</b>, {t.duplicates ?? 0} already in the app{t.failed ? `, ${t.failed} failed` : ""}.
        {imp.kpi ? ` ${imp.kpi.weeks_recalculated} KPI week(s) recalculated.` : ""}
      </div>
      {(imp.pages || []).filter((p) => p.error).map((p) => (
        <div key={p.page_id} className="rounded border border-rose-200 bg-rose-50 p-2 text-rose-700 break-words" data-testid={`meta-leads-import-error-${p.page_id}`}>
          {p.page_name || p.page_id}: {p.error}
        </div>
      ))}
      {imp.error && <div className="text-rose-700">{imp.error}</div>}
    </div>
  );
};

/** Facebook lead forms: live delivery status + the 90-day import. */
export const MetaLeadForms = ({ conns, onChanged }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/meta/leads/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = st?.import?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [running, load]);
  const start = async () => {
    setBusy(true);
    try {
      await api.post("/meta/leads/import");
      toast.success("Importing the last 90 days of lead forms…");
      load();
    } catch (e) {
      toast.error("Couldn't start the import", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  const live = conns.filter((c) => c.leadgen_subscribed);
  const d = st?.deliveries || {};
  return (
    <div className="space-y-2 rounded-md border border-zinc-200 bg-white p-3 text-xs" data-testid="meta-leads-panel">
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="h-4 w-4 text-[#1877F2]" />
        <span className="text-sm font-medium text-zinc-900">Facebook lead forms</span>
        <span className={live.length ? "text-emerald-700" : "text-amber-700"} data-testid="meta-leads-live">
          {live.length ? `Receiving new leads on ${live.length} Page(s)` : "Not receiving leads yet"}
        </span>
        <div className="flex-1" />
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={start} disabled={busy || running || !conns.length}
          data-testid="meta-leads-import-btn">
          {running ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Download className="mr-1 h-3 w-3" />}Import last 90 days
        </Button>
      </div>
      <p className="text-zinc-500">Each completed Instant Form becomes a new enquiry (channel “Meta lead ad”), and counts as a Meta paid lead in the KPI
        report. Imported past leads are added as closed enquiries dated when the form was sent.</p>
      {!live.length && conns.length > 0 && (
        <p className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-800" data-testid="meta-leads-setup-hint">
          To turn this on: in your Meta app add the <b>leads_retrieval</b> and <b>pages_manage_ads</b> permissions, tick <b>leadgen</b> under
          Webhooks → Page, and allow this app in Business Settings → Integrations → Leads Access. Then Disconnect and Connect the Page again.
        </p>
      )}
      <div className="text-zinc-600" data-testid="meta-leads-deliveries">
        Last 30 days: {d.imported_30d ?? 0} lead(s) added{d.failed_30d ? `, ${d.failed_30d} failed` : ""}
        {d.last_success_at ? ` · last ${fmtDateTime(d.last_success_at)}` : ""}
      </div>
      <LeadAccessCheck disabled={!conns.length} onChecked={onChanged} />
      <p className="text-zinc-500">To send yourself a real test lead, use Meta&apos;s Lead Ads Testing Tool
        (developers.facebook.com/tools/lead-ads-testing) — it should appear in Enquiries within a minute.</p>
      <Totals imp={st?.import} />
    </div>
  );
};
